import React from "react";
import { useTheme } from "./theme";

export default function Pricing() {
  const { theme } = useTheme();
  const palette = theme.palette;
  const [isMobile, setIsMobile] = React.useState(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia === "undefined"
    ) {
      return false;
    }
    return window.matchMedia("(max-width: 768px)").matches;
  });
  const [showScrollHint, setShowScrollHint] = React.useState(false);

  React.useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia === "undefined"
    ) {
      return;
    }
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    setIsMobile(mediaQuery.matches);

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  React.useEffect(() => {
    if (isMobile) {
      setShowScrollHint(true);
    } else {
      setShowScrollHint(false);
    }
  }, [isMobile]);

  const handleHireLinkClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("launchSafely:openHireTester"));
      }
    },
    []
  );

  const handleCarouselScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!showScrollHint) return;
      if (event.currentTarget.scrollLeft > 12) {
        setShowScrollHint(false);
      }
    },
    [showScrollHint]
  );

  const plans = [
    {
      name: "LaunchPad",
      price: "$19/mo",
      features: [
        "50 runs / month",
        "Core health checks & secondary warnings",
        "Test any public or private URL",
        "Full access to dashboard & run history",
        "Email support",
        "Human QA add-on (additional cost)",
      ],
    },
    {
      name: "Liftoff",
      price: "$49/mo",
      features: [
        "200 runs / month",
        "Everything in LaunchPad",
        "All check types & warning insights",
        "Slack alerts and webhooks",
        "Priority access to QA add-on (12h turnaround)",
      ],
      popular: true,
    },
    {
      name: "Orbit",
      price: "$99/mo",
      features: [
        "1000 runs / month",
        "Everything in LaunchPad + Liftoff",
        "Multi-browser & device coverage",
        "Custom alert routing & reports",
        "Includes 1 free human QA review each month",
      ],
    },
  ];

  return (
    <section
      id="pricing"
      style={{
        padding: "1rem 1rem",
        background: palette.backgroundAlt,
        color: palette.textPrimary,
        textAlign: "center",
      }}
    >
      <h2
        style={{
          fontSize: "2rem",
          fontWeight: 800,
          marginBottom: "2rem",
          color: palette.textPrimary,
        }}
      >
        Simple, Transparent Pricing
      </h2>

      <div
        style={{
          display: isMobile ? "flex" : "grid",
          gridTemplateColumns: isMobile
            ? undefined
            : "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "1.5rem",
          maxWidth: "1000px",
          margin: "0 auto",
          overflowX: isMobile ? "auto" : "visible",
          padding: isMobile ? "0 0.5rem 1rem" : undefined,
          scrollSnapType: isMobile ? "x mandatory" : undefined,
          WebkitOverflowScrolling: isMobile ? "touch" : undefined,
          scrollPadding: isMobile ? "0 1rem" : undefined,
        }}
        onScroll={isMobile ? handleCarouselScroll : undefined}
      >
        {plans.map((plan) => (
          <div
            key={plan.name}
            style={{
              background: palette.card,
              border: plan.popular
                ? `2px solid ${palette.accent}`
                : `1px solid ${palette.border}`,
              borderRadius: "16px",
              padding: "2rem 1.5rem",
              boxShadow: plan.popular
                ? "0 16px 32px rgba(47, 145, 209, 0.25)"
                : palette.shadow,
              position: "relative",
              textAlign: "left",
              flex: isMobile ? "0 0 85%" : undefined,
              minWidth: isMobile ? "85%" : undefined,
              scrollSnapAlign: isMobile ? "center" : undefined,
              scrollSnapStop: isMobile ? "always" : undefined,
            }}
          >
            {plan.popular && (
              <span
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  background: palette.accentGradient,
                  color: palette.textOnAccent,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  padding: "0.3rem 0.6rem",
                  borderRadius: "6px",
                }}
              >
                BEST FOR STARTUPS
              </span>
            )}
            <h3
              style={{
                fontSize: "1.25rem",
                fontWeight: 700,
                marginBottom: "0.5rem",
                color: palette.textPrimary,
              }}
            >
              {plan.name}
            </h3>
            <p
              style={{
                fontSize: "2rem",
                fontWeight: 800,
                color: palette.accentAlt,
                marginBottom: "1rem",
              }}
            >
              {plan.price}
            </p>
            <ul style={{ paddingLeft: "1.1rem", color: palette.textMuted }}>
              {plan.features.map((f) => (
                <li key={f} style={{ marginBottom: "0.5rem" }}>
                  {plan.name === "LaunchPad" &&
                  f.startsWith("Human QA add-on") ? (
                    <>
                      <a
                        href="#hire-tester"
                        onClick={handleHireLinkClick}
                        style={{
                          color: palette.accentAlt,
                          textDecoration: "none",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Human QA add-on
                      </a>{" "}
                      (additional cost)
                    </>
                  ) : (
                    f
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {showScrollHint && isMobile && (
        <div
          style={{
            marginTop: "0.25rem",
            fontSize: "0.85rem",
            color: palette.textMuted,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.4rem",
            userSelect: "none",
          }}
        >
          <span
            aria-hidden="true"
            style={{ fontSize: "1rem", letterSpacing: "0.2rem" }}
          >
            {"< >"}
          </span>
          Swipe to compare plans
        </div>
      )}
    </section>
  );
}
